// TanStack Query hooks for the Documents & Vault-metadata module. The
// `documents` rows are the metadata catalog over the file vault; the bytes
// live on the storage share behind the API. Uploads send the raw file body
// (Content-Type + X-Filename + query-param metadata) — they are NOT a JSON
// POST — so the create hook drives the dedicated uploadFile() client helper and
// builds a query string from the metadata. Downloads stream the stored bytes
// via downloadBlob(). Mirrors packages/api/src/routes/documents.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DocumentType,
  DocumentWithRefs,
  DocumentResponse,
  DocumentsListResponse,
  UploadDocumentInput,
  UpdateDocumentInput,
  DocumentFolderEventType,
  DocumentFolderAuditEntry,
  DocumentFolderAuditListResponse,
  OrganizationsListResponse,
  SitesListResponse,
} from '@dsr/shared-types';
import { api, uploadFile, downloadBlob } from '../client';

// ---------- Query keys ----------

const KEY = 'documents';

export const documentKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: DocumentFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  folderAudit: (filters: FolderAuditFilters) =>
    [KEY, 'folder-audit', { ...filters }] as const,
};

// ---------- Documents ----------

export interface DocumentFilters {
  organization_id?: number;
  site_id?: number;
  personnel_id?: number;
  detail_id?: number;
  incident_id?: number;
  document_type?: DocumentType;
  /** Surface documents expiring on or before this epoch-ms instant. */
  expiring_before?: number;
}

function buildDocumentQuery(filters: DocumentFilters): string {
  const params = new URLSearchParams();
  if (filters.organization_id !== undefined) {
    params.set('organization_id', String(filters.organization_id));
  }
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.personnel_id !== undefined) {
    params.set('personnel_id', String(filters.personnel_id));
  }
  if (filters.detail_id !== undefined) params.set('detail_id', String(filters.detail_id));
  if (filters.incident_id !== undefined) {
    params.set('incident_id', String(filters.incident_id));
  }
  if (filters.document_type) params.set('document_type', filters.document_type);
  if (filters.expiring_before !== undefined) {
    params.set('expiring_before', String(filters.expiring_before));
  }
  return params.toString();
}

export function useDocuments(filters: DocumentFilters = {}) {
  const qs = buildDocumentQuery(filters);
  const url = qs.length > 0 ? `/api/documents?${qs}` : '/api/documents';
  return useQuery({
    queryKey: documentKeys.list(filters),
    queryFn: ({ signal }) => api.get<DocumentsListResponse>(url, signal),
  });
}

export function useDocument(id: number | null) {
  return useQuery({
    queryKey: documentKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<DocumentResponse>(`/api/documents/${id}`, signal),
  });
}

// Upload: the file bytes are the request body and metadata travels as query
// params (mirrors the raw-body POST /api/documents route). The hook serializes
// the metadata into a query string, then hands the File to uploadFile() which
// sets Content-Type + X-Filename and runs the standard auth/refresh flow.
export interface UploadDocumentArgs extends UploadDocumentInput {
  file: File;
}

function buildUploadQuery(meta: UploadDocumentInput): string {
  const params = new URLSearchParams();
  if (meta.organization_id != null) {
    params.set('organization_id', String(meta.organization_id));
  }
  if (meta.site_id != null) params.set('site_id', String(meta.site_id));
  if (meta.personnel_id != null) params.set('personnel_id', String(meta.personnel_id));
  if (meta.detail_id != null) params.set('detail_id', String(meta.detail_id));
  if (meta.incident_id != null) params.set('incident_id', String(meta.incident_id));
  if (meta.document_type) params.set('document_type', meta.document_type);
  if (meta.title != null && meta.title.trim() !== '') {
    params.set('title', meta.title.trim());
  }
  if (meta.expires_at != null) params.set('expires_at', String(meta.expires_at));
  return params.toString();
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, ...meta }: UploadDocumentArgs) => {
      const qs = buildUploadQuery(meta);
      const url = qs.length > 0 ? `/api/documents?${qs}` : '/api/documents';
      return uploadFile<DocumentResponse>(url, file);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentKeys.lists() });
      void qc.invalidateQueries({ queryKey: [KEY, 'folder-audit'] });
    },
  });
}

export interface UpdateDocumentArgs extends UpdateDocumentInput {
  id: number;
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateDocumentArgs) =>
      api.patch<DocumentResponse>(`/api/documents/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: documentKeys.lists() });
      void qc.invalidateQueries({ queryKey: documentKeys.detail(id) });
    },
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/documents/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentKeys.lists() });
      void qc.invalidateQueries({ queryKey: [KEY, 'folder-audit'] });
    },
  });
}

// Download streams the stored bytes through the authenticated blob helper,
// triggering a browser save with a sensible filename. Not a query — it's a
// one-shot imperative action callers invoke from a button.
export function downloadDocument(doc: Pick<DocumentWithRefs, 'id' | 'title'>): Promise<void> {
  const filename = (doc.title && doc.title.trim() !== '' ? doc.title : `document-${doc.id}`)
    .replace(/[^\w. -]/g, '_');
  return downloadBlob(`/api/documents/${doc.id}/download`, filename);
}

// ---------- Folder lifecycle journal (append-only) ----------

export interface FolderAuditFilters {
  event_type?: DocumentFolderEventType;
}

export function useDocumentFolderAudit(filters: FolderAuditFilters = {}) {
  const params = new URLSearchParams();
  if (filters.event_type) params.set('event_type', filters.event_type);
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/documents/folders/audit?${qs}` : '/api/documents/folders/audit';
  return useQuery({
    queryKey: documentKeys.folderAudit(filters),
    queryFn: ({ signal }) =>
      api.get<DocumentFolderAuditListResponse>(url, signal),
  });
}

export interface RecordFolderEventInput {
  rel_path: string;
  event_type: DocumentFolderEventType;
  detail?: string | null;
}

export function useRecordFolderEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordFolderEventInput) =>
      api.post<{ event: DocumentFolderAuditEntry }>(
        '/api/documents/folders/event',
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, 'folder-audit'] });
    },
  });
}

// ---------- Parent lookups (org / site pickers + name resolution) ----------
//
// Populate the upload/edit form parent selectors and resolve a document's
// org/site name for display. Kept local to this module (dedicated org/site
// query modules are wired during Integration). Cached longer than the default
// since the option lists change rarely.

export function useOrganizationsLookup() {
  return useQuery({
    queryKey: [KEY, 'organizations-lookup'],
    queryFn: ({ signal }) =>
      api.get<OrganizationsListResponse>('/api/clients', signal),
    staleTime: 60_000,
  });
}

export function useSitesLookup() {
  return useQuery({
    queryKey: [KEY, 'sites-lookup'],
    queryFn: ({ signal }) => api.get<SitesListResponse>('/api/sites', signal),
    staleTime: 60_000,
  });
}

// Re-export the wire types callers most often need alongside the hooks.
export type { DocumentType, DocumentWithRefs, DocumentFolderAuditEntry };
